/**
 * Checkout mini-FAQ content — the five questions the concierge desk actually
 * gets at the review step, answered in the knowledge base's own words.
 *
 * PARITY RULE: every claim here must also be true in DEFERA_KNOWLEDGE_BASE
 * (server/tommy-concierge.js) so the desk never contradicts the page — the
 * grounding tests assert the load-bearing phrases in both places. Never say a
 * trip is "booked/ticketed with the airline" at checkout, and never explain
 * Defera Credit eligibility beyond "select bookings / eligibility may vary".
 *
 * The seventh question, about Faye travel insurance, shows only where the
 * launch flag is on AND checkout offers Faye on this trip: see
 * checkoutFaqItems(). Never tie the policy to the airline booking or say when
 * it is bought, and send coverage questions and claims to Faye.
 */
import { FAYE_INSURANCE_ENABLED } from '../../utils/fayeFlag.js';
import { FAYE_SUPPORT } from '../../../supabase/functions/_shared/trip-insurance.js';
import { FAYE_RULES } from '../../../supabase/functions/_shared/faye-pricing.js';

export const CHECKOUT_FAQ = [
  {
    id: 'pay_today',
    q: 'What do I pay today, and when is the rest due?',
    a: "Just the deposit shown above — that's what your card is charged today. The remaining balance is split into automatic weekly or bi-weekly installments, and every plan finishes before you fly. From your trip page you can pay ahead, pay it off early, or switch between weekly and bi-weekly at any time.",
  },
  {
    id: 'credit_check',
    q: 'Is there a credit check or interest?',
    a: 'No credit check, no interest, no hidden fees. Your deposit secures your agreed-upon trip price, and that price is protected from future airfare increases while your payment plan stays in good standing.',
  },
  {
    id: 'fees',
    q: 'What are the service fee and hold fee?',
    a: 'The service fee covers dedicated support throughout your payment plan, so you are never on your own. The hold fee covers Defera funding your full fare upfront so you can pay over time. Both are itemized in the Price Breakdown above, and your trip total already includes them — there is nothing extra to pay later.',
  },
  {
    id: 'cancel_change',
    q: 'What if I need to cancel or change my trip?',
    a: 'Dates and times on the same route can be changed in the app on eligible fares. Cancellation terms depend on your fare: they are set out in the Cancellation & Refund Policy you agree to below and shown again in the app before you confirm any cancellation. Defera Credit is offered on select bookings, and eligibility may vary.',
  },
  {
    id: 'seats_bags',
    q: 'Can I add seats or checked bags later?',
    a: 'Yes. Seats and checked bags can be added at any time after booking, whether your plan is still running or paid in full. From your trip page, Flight Options has Purchase Seats and Purchase Baggage: seats open a live seat map on supported airlines and fares, and bags show live pricing you pay in one go, separate from your installment plan. Where a live seat map is not available, the same button hands you to our concierge team, who arrange seats for you.',
  },
  {
    id: 'confirmation',
    q: 'When do I get my airline confirmation number?',
    a: 'Once your plan is paid in full and all release conditions are met, your airline confirmation number appears on your Trips page and the airline emails your e-ticket. Until then, your agreed-upon trip price stays secured while you remain in good standing.',
  },
];

/**
 * The travel insurance question. Parity: the desk's "Travel insurance by
 * Faye" knowledge (server/faye-knowledge.js) states every fact in it.
 */
export const CHECKOUT_FAQ_TRAVEL_INSURANCE = {
  id: 'travel_insurance',
  q: 'How does the travel insurance work?',
  a: `It's optional, and it comes from Faye. You pay exactly Faye's price for the plan, with no Defera fees added. It's included in your trip total and spread across your payments after the deposit, so your deposit today doesn't change. If you'd rather decide later, you can add it from your trip page until ${FAYE_RULES.fullCoverageMinDaysBeforeDeparture} days before departure. Faye issues your policy before you travel, and your policy number appears on your trip page once your trip is paid in full. If you change your flight, it's re-priced for the new flight: an increase is added to what you pay today for the change, and a decrease goes back to your card. If you cancel, the cancel screen shows what happens to the insurance part, and where it's refunded, it goes back to your card. Defera is a travel retailer for Faye, not a licensed insurance agent: we can describe the plans and their price, but technical questions about your coverage, whether your existing insurance is enough, and claims go to Faye at ${FAYE_SUPPORT.email} or ${FAYE_SUPPORT.phoneUs}.`,
};

/**
 * The questions for one checkout: the six standing ones, plus the travel
 * insurance one only when the Faye launch flag is on and checkout offers
 * insurance on this trip. Review passes `insuranceOffered` through
 * <CheckoutFaq insuranceOffered />. `enabled` defaults to the flag; tests
 * set it to check both states.
 */
export function checkoutFaqItems({ insuranceOffered = false, enabled = FAYE_INSURANCE_ENABLED } = {}) {
  return enabled && insuranceOffered ? [...CHECKOUT_FAQ, CHECKOUT_FAQ_TRAVEL_INSURANCE] : CHECKOUT_FAQ;
}

/** Phrases the page must never carry (mirrors the desk's hard rules). */
export const CHECKOUT_FAQ_BANNED_PHRASES = [
  /ticket was ever issued/i,
  /booked with the airline/i,
  /held with the airline/i,
  /ticketed immediately/i,
  /guaranteed upgrade/i,
  // Travel insurance (Hard Rules #15/#17, Faye's retailer disclosure): never
  // tie the policy to ticketing, never call insurance required, and never
  // call it a "damage waiver" (California).
  /\b(?:insurance|your policy|the policy)\b[^.]{0,80}\bticket/i,
  /\bticket(?:ed|ing)?\b[^.]{0,80}\b(?:insurance|your policy|the policy)\b/i,
  /insurance is required|required to (?:buy|purchase|add) (?:travel )?insurance/i,
  /damage waiver/i,
];
